import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import apiService from '../../services/api';
import { createLogger, adminLogContext } from '../../utils/logger'

const logger = createLogger('MunicipalityAssignments')
const ctx = (extra) => adminLogContext('municipality_assignments', extra)


const MunicipalityAssignments = () => {
  const [counties, setCounties] = useState([]);
  const [selectedCountyId, setSelectedCountyId] = useState('');
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [municipalityStatus, setMunicipalityStatus] = useState({}); // { county_id: boolean }

  const [loadingOverlaps, setLoadingOverlaps] = useState(false);
  const [saving, setSaving] = useState(false);

  const [rows, setRows] = useState([]);
  const [rowsSource, setRowsSource] = useState(''); // 'existing' or 'computed'
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchCountiesAndStatus = async () => {
      try {
        setLoadingCounties(true);
        setError('');
        
        // Fetch counties and municipality status in parallel
        const [countiesResp, statusResp] = await Promise.all([
          apiService.getAllCounties(),
          apiService.getCountiesMunicipalityStatus(),
        ]);
        
        setCounties(countiesResp.counties || []);
        setMunicipalityStatus(statusResp.status || {});
      } catch (err) {
        logger.apiFailure('Failed to load counties', ctx({ action: 'load_counties' }), err);
        setError(err?.message || 'Failed to load counties.');
      } finally {
        setLoadingCounties(false);
      }
    };
    fetchCountiesAndStatus();
  }, []);

  const handleCountyChange = async (event) => {
    const countyId = event.target.value;
    setSelectedCountyId(countyId);
    setRows([]);
    setRowsSource('');
    setSuccessMessage('');
    setError('');

    if (!countyId) return;

    // Fetch existing assignments for this county
    try {
      const existingAssignments = await apiService.getExistingMunicipalityAssignments(countyId);
      if (existingAssignments && existingAssignments.length > 0) {
        // Sort by overlap_pct descending
        existingAssignments.sort((a, b) => b.overlap_pct - a.overlap_pct);
        setRows(existingAssignments);
        setRowsSource('existing');
      }
    } catch (err) {
      logger.debug('Could not load existing municipality assignments', ctx({
        action: 'load_existing_assignments',
        county_id: String(countyId),
      }), err);
    }
  };

  const handleComputeOverlaps = async () => {
    if (!selectedCountyId) return;
    try {
      setLoadingOverlaps(true);
      setError('');
      setSuccessMessage('');

      const data = await apiService.getMunicipalityOverlaps(selectedCountyId);
      // Sort by overlap_pct descending
      if (data) data.sort((a, b) => b.overlap_pct - a.overlap_pct);
      setRows(data || []);
      setRowsSource('computed');
    } catch (err) {
      logger.apiFailure('Failed to compute municipality overlaps', ctx({
        action: 'compute_municipality_overlaps',
        county_id: String(selectedCountyId),
      }), err);
      setError(err?.message || 'Failed to compute municipality overlaps.');
    } finally {
      setLoadingOverlaps(false);
    }
  };

  const handleToggleAssigned = (geoid) => {
    setRows((prev) =>
      prev.map((row) =>
        row.municipality_geoid === geoid
          ? { ...row, is_assigned: !row.is_assigned }
          : row
      )
    );
  };

  const handleSave = async () => {
    if (!selectedCountyId || rows.length === 0) return;
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      await apiService.saveMunicipalityAssignments(selectedCountyId, rows);
      setSuccessMessage('Municipality assignments saved successfully.');
      
      // Update the municipality status for this county based on saved assignments
      const hasAssigned = rows.some((row) => row.is_assigned);
      setMunicipalityStatus((prev) => ({
        ...prev,
        [selectedCountyId]: hasAssigned,
      }));
    } catch (err) {
      logger.apiFailure('Failed to save municipality assignments', ctx({
        action: 'save_municipality_assignments',
        county_id: String(selectedCountyId),
        row_count: rows.length,
      }), err);
      setError(err?.message || 'Failed to save municipality assignments.');
    } finally {
      setSaving(false);
    }
  };

  const selectedCounty =
    counties.find((c) => c.id === selectedCountyId) || null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Municipality Assignments
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Select a county, compute municipality overlaps from TIGER, and then
        review which municipalities should be assigned to that county. Nothing
        is written to the database until you click <strong>Save assignments</strong>.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="county-select-label">County</InputLabel>
            <Select
              labelId="county-select-label"
              label="County"
              value={selectedCountyId}
              onChange={handleCountyChange}
              MenuProps={{
                disablePortal: true,
                PaperProps: {
                  sx: { zIndex: 9999 },
                },
              }}
            >
              {loadingCounties && (
                <MenuItem value="">
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
                </MenuItem>
              )}
              {!loadingCounties &&
                counties.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {municipalityStatus[c.id] ? (
                        <Tooltip title="Municipalities configured">
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="No municipalities configured">
                          <CancelIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                        </Tooltip>
                      )}
                      {c.name} ({c.state})
                    </Box>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            disabled={!selectedCountyId || loadingOverlaps}
            onClick={handleComputeOverlaps}
          >
            {loadingOverlaps ? 'Computing…' : 'Compute overlaps'}
          </Button>

          {selectedCounty && (
            <Typography variant="body2" sx={{ ml: 1 }}>
              Selected: {selectedCounty.name}, {selectedCounty.state}
            </Typography>
          )}
        </Box>
      </Paper>

      {rows.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">
              {rowsSource === 'existing' ? 'Saved assignments' : 'Overlapping municipalities'} ({rows.length})
              {rowsSource === 'existing' && (
                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  — click "Compute overlaps" to refresh from TIGER
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Tooltip title="Auto-select all municipalities with overlap ≥ 50%">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setRows((prev) =>
                      prev.map((row) => ({
                        ...row,
                        is_assigned: row.overlap_pct >= 50,
                      }))
                    )
                  }
                >
                  Auto-select ≥ 50%
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? 'Saving…' : 'Save assignments'}
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Include</TableCell>
                  <TableCell>Municipality</TableCell>
                  <TableCell>GEOID</TableCell>
                  <TableCell align="right">Overlap %</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const overridden =
                    row.is_assigned !== row.suggested_assigned;
                  return (
                    <TableRow key={row.municipality_geoid}>
                      <TableCell>
                        <Switch
                          checked={row.is_assigned}
                          onChange={() =>
                            handleToggleAssigned(row.municipality_geoid)
                          }
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{row.municipality_name}</TableCell>
                      <TableCell>{row.municipality_geoid}</TableCell>
                      <TableCell align="right">
                        {row.overlap_pct.toFixed(1)}
                      </TableCell>
                      <TableCell>
                        {overridden
                          ? 'Manually overridden'
                          : row.suggested_assigned
                          ? 'Auto: ≥ 50%'
                          : 'Auto: &lt; 50%'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default MunicipalityAssignments;


